export type BankAccount = {
  accountId: number;
  iban: string;
  accountName: string;
  bank: Bank;
  businessId: number;
  balance: number; // in cents
  requisitionId?: string;
  requisitionExpiryDate?: Date;
  transferCompleted: boolean;
  latestFetching?: Date;
  goCardlessAccountId?: string;
};

export enum Bank {
  ABN_AMRO = 'ABN_AMRO',
  ASN_BANK = 'ASN_BANK',
  BUNQ = 'BUNQ',
  ING = 'ING',
  RABOBANK = 'RABOBANK',
  SNS_BANK = 'SNS_BANK',
}

export enum TransactionType {
  // positive sum that is assigned to a (group of) member(s); does not change group balance; changes personal balance
  PERSONAL_INCOME = 'PERSONAL_INCOME',

  // negative sum that is assigned to a (group of) member(s); does not change group balance; changes personal balance
  PERSONAL_EXPENSE = 'PERSONAL_EXPENSE',

  // negative sum that has no assignees; changes group balance; does not change personal balance
  GROUP_EXPENSE = 'GROUP_EXPENSE',

  // positive sum that has no assignees; changes group balance; does not change personal balance
  GROUP_INCOME = 'GROUP_INCOME',

  // a type of group income -- in the future this can become its own model
  // positive sum that has one assignee; changes group balance; does not change personal balance
  CONTRIBUTION = 'CONTRIBUTION',

  // internal transfer between two bank accounts of the same business; excluded from P&L
  INTERNAL_BANK_TRANSFER = 'INTERNAL_BANK_TRANSFER',
}

export type CreateBankAccountModel = Omit<
  BankAccount,
  | 'requisitionId'
  | 'requisitionExpiryDate'
  | 'transferCompleted'
  | 'accountId'
  | 'businessId'
  | 'balance'
>;

export type BankMutation = {
  mutationId: number;
  accountId: number;
  sequenceNumber: number;
  amount: number; // in cents
  balanceAfter: number; // in cents
  description: string;
  oppositeName: string;
  oppositeAccount: string;
  transactionDate: Date;

  // Details from GoCardless
  bankTransactionId?: string;
  goCardLessTransactionId?: string;
  externalData?: object;

  // Details for Prisma
  bankTransactionCount: number;
};

export type CreateBankMutationModel = Omit<
  BankMutation,
  'mutationId' | 'accountId' | 'bankTransactionCount' | 'sequenceNumber'
>;

export type BankTransaction = {
  transactionId: number;
  businessId: number;
  payerId?: number;
  processedByConnectionId?: number;
  mutationId?: number;
  contributionPaymentId?: number;
  transactionType: TransactionType;
  amount: number;
  date: Date;
  description: string;
  contributionPayment?: {
    contributionPlanId: number;
    connectionId: number;
  };
  processedByConnection?: {
    connectionId: number;
    user: {
      userId: number;
      firstName: string;
      lastName: string;
      picture: string | null;
    };
  } | null;
};

export type BankTransactionWithParticipationModel = BankTransaction & {
  participations?: BankTransactionParticipation[];
};

export type BankAccountWithTransactions = BankAccount & {
  transactions: BankTransaction[];
};

export type CreateBankTransactionModel = Omit<BankTransaction, 'transactionId' | 'businessId'> & {
  participations?: BankTransactionParticipation[];
  contributionPayment?: {
    contributionPlanId: number;
    connectionId: number;
  };
};

export type BankTransactionWithParticipation = BankTransaction & {
  participations: BankTransactionParticipation[] | null;
};

export type InternalBankTransfer = {
  id: number;
  businessId: number;
  fromTransactionId?: number;
  toTransactionId?: number;
};

export type BankTransactionParticipation = {
  connectionId: number;
  amount: number;
};

export interface GoCardlessInstitution {
  id: string;
  name: string;
  logo: string;
  countries: string[];

  bic?: string;
  transaction_total_days?: string;
  supported_features?: string[];
}

export interface CreateGoCardlessLinkBody {
  redirect: string;
  institution_id: string;
  user_language: 'en' | 'nl';
  agreement?: string;
}

export interface GoCardlessLink {
  redirect: string;
  link: string;
  institution_id: string;

  id: string;
  created: string; // ISO Date
  status: 'CR' | 'ID' | 'LN' | 'RJ' | 'ER' | 'SU' | 'EX' | 'GC' | 'UA' | 'GA' | 'SA';
  agreement: string;
  reference: string;
  accounts: string[];
  user_language: 'en' | 'nl';
  ssn: string;
  account_selection: boolean;
  redirect_immediate: boolean;
}

export interface GoCardlessAccount {
  id: string;
  created: string; // ISO Date
  last_accessed: string; // ISO Date
  iban: string;
  status: string;
  institution_id: string;
  owner_name: string;
  name: string;
}

export enum ContributionFrequency {
  ONCE = 'ONCE',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  YEARLY = 'YEARLY',
}

export interface ContributionPlanModel {
  contributionPlanId: number;
  name: string;
  description: string;
  amount: number; // in cents, full amount
  frequency: ContributionFrequency;
  startDate: Date;
  // endDate?: Date;
  numberOfInstallments: number;
  businessId: number;

  // contributionPayments ContributionPayment[]
  // appliesTo            ContributionPlanOnMembers[]
}

export type ContributionPlanWithMembersModel = ContributionPlanModel & {
  appliesTo: { connectionId: number }[];
};

export type CreateContributionPlanDto = Omit<
  ContributionPlanModel,
  'contributionPlanId' | 'businessId'
> & {
  appliesToConnectionIds: number[];
};

export interface ContributionPlanOnMembersModel {
  contributionPlanId: number;
  connectionId: number;

  // contributionPlan ContributionPlan
  // connection       B2CConnection
}

export interface ContributionPaymentModel {
  contributionPaymentId: number;
  contributionPlanId: number;
  connectionId: number;
  transactionId: number;

  // contributionPlan ContributionPlan @relation(fields: [contributionPlanId], references: [contributionPlanId], onDelete: Cascade)
  // connection       B2CConnection    @relation(fields: [connectionId], references: [connectionId], onDelete: Cascade)
  // transaction      BankTransaction  @relation(fields: [transactionId], references: [transactionId], onDelete: Cascade)
}

export type CreateContributionPaymentDto = Omit<ContributionPaymentModel, 'contributionPaymentId'>;

export type ContributionPaymentWithTransactionDetails = ContributionPaymentModel & {
  transaction: BankTransaction;
};

export type ContributionPlanWithPayments = ContributionPlanWithMembersModel & {
  contributionPayments: ContributionPaymentWithTransactionDetails[];
};

export type BalanceStats = {
  income: number;
  expenses: number;
  balance: number;
};

export type BalanceSheet = {
  assets: {
    bankAccounts: {
      accounts: {
        accountName: string;
        balance: number;
      }[];
      totalBalance: number;
    };
    receivables: {
      negativePersonalBalance: number;
      contributionReceivables: number;
      totalReceivables: number;
    };
    unassigned: {
      unassignedExpenses: number;
      totalUnassigned: number;
    };
    totalAssets: number;
  };
  liabilitiesAndEquity: {
    equity: {
      initialEquity: number;
      profitSinceStart: number;
      totalEquity: number;
    };
    liabilities: {
      positivePersonalBalance: number;
      contributionOverPayments: number;
      totalLiabilities: number;
    };
    unassigned: {
      unassignedIncome: number;
      totalUnassigned: number;
    };
    totalLiabilitiesAndEquity: number;
  };
};

export interface ProfitAndLoss {
  groupIncome: number; // in cents
  groupExpenses: number; // in cents (likely negative amounts)
  contributionExpenses: number; // in cents
}
